import * as React from "react";
import { cn } from "@lib/utils";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** DECLARED BUT DELIBERATELY NOT STYLED (2026-09-02). 34 call sites pass size="sm"
   *  and 12 pass variant=... — and this component has never consumed either: they were
   *  forwarded to the DOM as invalid <button> attributes and ignored by every browser,
   *  so every "small"/"variant" button has always rendered identically to the default.
   *  Nobody ever saw a type error because @types/react was missing and every component
   *  prop typed as `any`.
   *
   *  Absorbing them here keeps the DOM clean and the types honest WITHOUT changing how
   *  a single button renders. Actually styling them is a page-wide visual change across
   *  every dashboard — that belongs to the deferred system-wide UI pass, with CIO eyes
   *  on it, not to a typecheck fix. */
  size?: "sm" | "default";
  variant?: "primary" | "secondary" | "outline";
}

// Styled 2026-09-02 (CIO-directed), after two days of being declared-but-absorbed. The
// call sites had encoded intent for months — size="sm" on 34 dense-toolbar buttons,
// variant on 12 — and none of it ever rendered because the props were dropped. Mapping:
//   sm        compact paddings + text-xs, for row toolbars and dense lanes
//   primary   the accent fill — the one emphasized action in a group
//   outline   transparent fill, stronger border — secondary action beside a primary
//   secondary the existing default look (explicitly nameable)
// Everything routes through the same theme variables the default always used, so both
// themes hold without new colour literals.
const SIZE_CLASSES: Record<string, string> = {
  sm: "px-2.5 py-1 text-xs",
  default: "px-4 py-2 text-sm",
};
const VARIANT_CLASSES: Record<string, string> = {
  primary:
    "bg-[rgb(var(--c-accent))] text-white border-transparent hover:opacity-90 active:opacity-80",
  outline: "bg-transparent border-[var(--border-color)]",
  secondary: "",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, type = "button", size = "default", variant = "secondary", ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium shadow-sm transition-colors",
          SIZE_CLASSES[size] ?? SIZE_CLASSES.default,
          "border border-[var(--border-color)]",
          variant !== "primary" && "bg-[var(--button-bg)] text-[var(--button-text)]",
          variant !== "primary" &&
            "hover:bg-[var(--button-bg-hover)] active:bg-[var(--button-bg-hover)]",
          VARIANT_CLASSES[variant] ?? "",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nav-hover)] focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button };